import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Accordion from '@mui/material/Accordion';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { MiniScore } from '../MiniScore';
import { CategoryGroup, ContractExecution } from '@tech-insights/workflows-sdk';
import { useTheme } from '@mui/material/styles';
import OpenInNew from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import { FactValueDisplay } from '../FactValueDisplay/FactValueDisplay';

export type CategoryDetailsProps = {
  category: CategoryGroup;
};

export const CategoryDetails = ({
  category,
}: Readonly<CategoryDetailsProps>) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <>
      {category?.executions?.map(
        (item: ContractExecution, contractIndex: number) => (
          <Accordion
            key={`${item?.contract?.name}-${contractIndex}`}
            sx={{
              marginTop: 0,
              marginBottom: 0,
              '&.Mui-expanded': {
                marginTop: 0,
                marginBottom: 0,
              },
              '&:before': {
                display: 'none', // Remove the default border
              },
            }}
          >
            <AccordionSummary
              sx={{
                borderTop: isDarkMode
                  ? '1px solid rgba(255, 255, 255, 0.5)'
                  : '1px solid rgba(0, 0, 0, 0.12)',
              }}
              expandIcon={<ExpandMoreIcon />}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '90%',
                }}
              >
                <Typography variant="h5">{item?.contract?.name}</Typography>
                <Typography variant="body2">
                  {item?.contract?.description}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignSelf: 'center' }}>
                <Box sx={{ alignSelf: 'center', marginRight: '8px' }}>
                  <MiniScore
                    achievementPercentage={item?.result?.success ? 100 : 0}
                  />
                </Box>
                <Typography data-testid="category-detail-accordion-summary-success">
                  {item?.result?.success ? 'Passed' : 'Failed'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {item?.result?.checks?.map((check, checkIndex: number) => (
                <Card key={`${check?.fact}-${checkIndex}`}>
                  <CardHeader
                    title={check?.fact}
                    subheader={
                      (check?.metadata as { description?: string })
                        ?.description ?? ''
                    }
                  />
                  <CardContent>
                    <Box sx={{ display: 'flex' }}>
                      <Typography
                        sx={{ marginRight: '16px', width: '10%' }}
                        variant="body1"
                      >
                        Result:
                      </Typography>
                      <Box sx={{ alignSelf: 'center', marginRight: '8px' }}>
                        <MiniScore
                          achievementPercentage={check?.result ? 100 : 0}
                        />
                      </Box>
                      <Typography variant="body1">
                        {check?.result ? 'passed' : 'failed'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      <Typography
                        sx={{ marginRight: '16px', width: '10%' }}
                        variant="body1"
                      >
                        Value:
                      </Typography>
                      <Typography
                        sx={{ alignSelf: 'center', marginRight: '8px' }}
                      >
                        <FactValueDisplay check={check} />
                      </Typography>
                    </Box>
                    {!!(check?.metadata as { hint?: string })?.hint && (
                      <Box sx={{ display: 'flex' }}>
                        <Typography
                          sx={{ marginRight: '16px', width: '10%' }}
                          variant="body1"
                        >
                          Hint:
                        </Typography>
                        <Typography
                          sx={{ alignSelf: 'center', marginRight: '8px' }}
                        >
                          {(check?.metadata as { hint?: string })?.hint ?? ''}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex' }}>
                      <Typography
                        sx={{ marginRight: '16px', width: '10%' }}
                        variant="body1"
                      >
                        Suggestion:
                      </Typography>
                      <Typography
                        sx={{ alignSelf: 'center', marginRight: '8px' }}
                      >
                        {(check?.metadata as { suggestion?: string })
                          ?.suggestion ?? ''}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions
                    sx={{
                      justifyContent: 'flex-end',
                      p: 0,
                      mt: 'auto',
                      mr: 1,
                      mb: 1,
                    }}
                  >
                    <Button
                      size="small"
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNew />}
                      data-testid="category-detail-link-out"
                      href={
                        (check?.metadata as { documentation?: string })
                          ?.documentation ?? '#'
                      }
                      sx={{ minWidth: 'auto', p: '4px 8px' }}
                    >
                      Docs
                    </Button>
                  </CardActions>
                </Card>
              ))}
            </AccordionDetails>
          </Accordion>
        ),
      )}
    </>
  );
};
